import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import imageUrlBuilder from '@sanity/image-url';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import sanityClient from '../client';

import './SinglePost.css';

const debounce = (fn, ms = 0) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

const builder = imageUrlBuilder(sanityClient);

const uuid = () => {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

function urlFor(source) {
  return builder.image(source);
}

export default function SinglePost() {
  const [singlePost, setSinglePost] = useState({});
  const [menu, setMenu] = useState({
    data: [],
    cur: ''
  });
  const mkdRef = useRef(null);
  const docAsideRef = useRef(null);
  const docAsideTopRef = useRef(null);
  const isRedirect = useRef(false);
  const { slug } = useParams();

  useEffect(() => {
    sanityClient.fetch(`*[slug.current == "${slug}"]{
      title,
      _id,
      slug,
      mainImage{
        asset->{
          _id,
          url,
        },
      },
      body,
      "name": author->name,
      "authorImage": author->image
    }`).then((data) => setSinglePost(data[0]))
    .catch(console.error)
  }, [slug]);

  const setupData = () => {
    const nodeInfo = [];
    if(!mkdRef.current) {
      return;
    }
    setTimeout(() => {
      mkdRef.current.childNodes.forEach(item => {
        if (['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(item.nodeName)) {
          const id = uuid();
          item.setAttribute('id', id);
          nodeInfo.push({
            type: item.nodeName, 
            id, 
            text: item.textContent,
            top: item.getBoundingClientRect().top + document.documentElement.scrollTop,
            el: item, 
          });
        }
      });
      if(nodeInfo.length) {
        setMenu(menu => ({
          ...menu,
          data: nodeInfo,
          cur: menu.cur || nodeInfo[0].id,
        }))
      }
    }, 300);
  }

  useEffect(() => {
    setupData();
    window.onload = setupData;
  }, [])

  const setMenuPosition = (scrollTop) => {
    if(!docAsideRef.current) {
      return;
    }
    let headerHeight = document.querySelector('#root > header').offsetHeight;
    if(!docAsideTopRef.current) {
      docAsideTopRef.current = docAsideRef.current.getBoundingClientRect().top + scrollTop;
    }
    if(docAsideTopRef.current - headerHeight < scrollTop) {
      docAsideRef.current.style.top = `${scrollTop + headerHeight - docAsideTopRef.current + 10}px`;
    } else {
      docAsideRef.current.style.top = '0px';
    }
  }

  const isPageBottom = () => {
    const scrollTop = document.documentElement.scrollTop;
    return document.documentElement.clientHeight + scrollTop >= document.documentElement.scrollHeight
  }

  const setCurrentMenu = useCallback((scrollTop) => {
    let cur = null;
    for(let item of menu.data) {
      if (scrollTop >= item.top) {
        cur = item.id;
      } else {
        break;
      }
    }
    if (isPageBottom()) {
      cur = menu.data[menu.data.length - 1].id;
    }
    if(cur) {
      setMenu(menu => ({ ...menu, cur }))
    }
  }, [menu.data]);

  const scrollPage = (item) => {
    isRedirect.current = true;
    const scrollTop = document.documentElement.scrollTop;
    const headerHeight = document.querySelector('#root > header').offsetHeight;
    const newTop = item.el.getBoundingClientRect().top + scrollTop;
    window.scrollTo(0, newTop);
    const fixedTop = (item.el.getBoundingClientRect().top < headerHeight) && (item.el.getBoundingClientRect().top > 0) ? newTop - headerHeight : newTop;
    window.scrollTo(0, fixedTop);
    setTimeout(() => {
      isRedirect.current = false;
    }, 1000);
  }

 useEffect(() => {
    if(menu.data.length) {
      setMenuPosition(document.documentElement.scrollTop);
    }
    document.body.onscroll = debounce(() => {
      if(!menu.data.length) {
        return;
      }
      const scrollTop = document.documentElement.scrollTop;
      setMenuPosition(scrollTop);
      if(isRedirect.current) {
        return;
      }
      setCurrentMenu(scrollTop);
    }, 300);
  }, [menu.data.length, setCurrentMenu]);

  return (
    <>
      <main className="bg-gray-200 min-h-screen p-12 relative z-0 single-post">
        <article className="container shadow-lg mx-auto bg-green-100 rounded-lg">
          {singlePost && (
            <>
              <header className="relative">
                <div className="absolute h-full w-full flex items-center justify-center p-8">
                  <div className="bg-white bg-opacity-75 rounded p-12">
                    <h1 
                      className="cursive text-3xl lg:text-6xl mb-4" 
                      style={{textShadow: '0 0 10px #000'}}
                    >
                      {singlePost.title}
                    </h1>
                    <div className="flex justify-center text-gray-800">
                      <img 
                        src={urlFor(singlePost.authorImage).url()} 
                        alt={singlePost.name}
                        className="w-10 h-10 rounded-full"
                      />
                      <p className="cursive flex items-center pl-2 text-2xl">{singlePost.name}</p>
                    </div>
                  </div>
                </div>
                <img 
                  src={singlePost.mainImage ? singlePost.mainImage.asset.url : ''}
                  alt={singlePost.title}
                  className="w-full object-cover rounded-t"
                  style={{height: 400}}
                />
              </header>
              <div 
                className="flex flex-row overflow-hidden px-10 lg:px-18 py-6 prose lg:prose-xl max-w-full justify-between"
              >
                <div className="papers" ref={mkdRef} style={{maxWidth: `calc(100% - ${48 + 12 * Math.max(...menu.data.map(item => item.text.length), 0)}px)`}}>
                  <ReactMarkdown plugins={[gfm]} children={singlePost.body} />
                </div>
                <section className={`doc-aside ${menu.data.length ? '' : 'hidden'}`}>
                  <div className="post-menu bg-black-800 rounded" ref={docAsideRef}>
                    {
                      menu.data.map((item) => {
                        return (
                          <div 
                            className={`${item.type}type post-menu-item`} 
                            key={item.id} 
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenu(menu => ({ ...menu, cur: item.id }));
                              scrollPage(item);
                            }} 
                          >
                            <span 
                              className={`text-red-100 font-bold cursor-pointer hover:underline hover:text-red-400 text-xs transition-all ${menu.cur === item.id ? 'text-red-300' : ''}`}
                            >
                              {item.text}
                            </span>
                          </div>
                        );
                      })
                    }
                  </div>
                </section>
              </div>
            </>
          )}
          {!singlePost && '加载中...'}
        </article>
      </main>
    </>
  )
}
